/**
* @copyright Copyright (C) 2021 Nile AI, Inc - All Rights Reserved
* Unauthorized copying of this file, via any medium is strictly prohibited
* Proprietary and confidential
*/

import React, {
  useEffect, useState,
} from 'react';
import Dialog from '@material-ui/core/Dialog';
import { KnSubtleText } from 'components/Typography';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import KnButton from 'components/Button';
import palette from 'styles/colors';
import { useTranslation } from 'react-i18next';
import KnEditIcon from 'components/icons/EditIcon';
import { KnActionLink } from 'components/Link';
import { styled, TextareaAutosize, Typography } from '@material-ui/core';
import useRegimenText from './emrExport/useRegimenText';

const KnDialogActions = styled(DialogActions)(({ theme }) => ({
  display: 'block',
  textAlign: 'center',
  padding: theme.spacing(6),
  paddingBottom: theme.spacing(3),
  paddingTop: theme.spacing(3),
  '& button:nth-child(2)': {
    marginLeft: 0,
    marginTop: theme.spacing(1),
  },
}));

const KnDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
}));

const KnTextCopyDialog = styled(Dialog)({
  '& .MuiDialog-paperWidthSm': {
    maxWidth: 600,
  },
});

const KnCopyTextArea = styled(TextareaAutosize)(({ theme }) => ({
  outline: 'none',
  border: 0,
  width: '100%',
  resize: 'none',
  maxHeight: 370,
  overflowY: 'scroll !important',
  padding: theme.spacing(2),
  color: palette.gunmetal,
}));

const KnCopySubtleText = styled(KnSubtleText)({
  fontSize: 14,
  textAlign: 'right',
  display: 'block',
});

/**
 * Medication Regiment to text
 */
const KnRegimenToText = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inputText, setInputText] = useState('');
  const { t: translate } = useTranslation();
  const { currentMedsText: defaultText } = useRegimenText({
    showTitle: false, extraSpace: false, notStarted: true,
  });

  useEffect(() => {
    setInputText(defaultText);
  }, [defaultText, setInputText, open]);


  const openTextDialog = () => {
    setOpen(true);
  };

  const onCloseCopyDialog = () => {
    setOpen(false);
  };

  const handleChange = (e) => {
    setInputText(e.target.value);
  };

  const onCopyText = () => {
    navigator.clipboard.writeText(inputText).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 1000);
    });
  };

  return (
    <>
      <KnActionLink
        onClick={openTextDialog}
        LhsIcon={KnEditIcon}
        data-testid="export-medication-to-text"
      >
        {translate('PATIENT_RECORD.medicationRegimen.exportRegimentToText')}
      </KnActionLink>
      <KnTextCopyDialog
        open={open}
        onClose={onCloseCopyDialog}
        data-testid="dialog-wrapper"
      >
        <KnDialogTitle data-testid="dialog-title">{translate('PATIENT_RECORD.medicationRegimen.regimenSummary')}</KnDialogTitle>
        <DialogContent data-testid="dialog-content">
          <KnCopyTextArea onChange={handleChange} rowsMin={15} value={inputText} />
          <Typography component={KnCopySubtleText}>
            {(inputText || '').trim().length}
            {' '}
            Characters
          </Typography>
        </DialogContent>
        <KnDialogActions data-testid="dialog-actions">
          <KnButton fullWidth onClick={onCopyText} data-testid="dialog-submit-button">
            {translate(`PATIENT_RECORD.medicationRegimen.${copied ? 'copied' : 'copyText'}`)}
          </KnButton>
          <KnButton fullWidth variant="text" onClick={onCloseCopyDialog} data-testid="dialog-cancel-button">
            {translate('ASSIGN_REGIMEN.returnToPatient')}
          </KnButton>
        </KnDialogActions>
      </KnTextCopyDialog>
    </>
  );
};

KnRegimenToText.defaultProps = {
};

KnRegimenToText.propTypes = {};

export default KnRegimenToText;
